import React, { useState } from "react";
import { Dialog } from '@progress/kendo-react-dialogs';
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@progress/kendo-react-inputs";
import styles from './AddRelationDialog.module.css';

function AddRelationDialog({
    onClose,
    onAdd,
    newRelation,
    onChange,
    entityOptions,
    relations,
    maxRelations,
    referenceAttributes = [], // new prop: array of attribute names
    referenceAttribute = '', // new prop: selected attribute
    onReferenceAttributeChange, // new prop: handler
    error // new prop: error message
}) {
    const { t } = useTranslation("create_project/alice_recommended_relations", { useSuspense: false });
    const { t: cardinalityT } = useTranslation("linked_entity/recommended_relation_card")
    const [addRelationError, setAddRelationError] = useState('');
    
    const relationTypeOptions = [
        { text: cardinalityT('cardinality.1-1'), value: 'one-to-one' },
        { text: cardinalityT('cardinality.1-*'), value: 'one-to-many' },
        { text: cardinalityT('cardinality.*-*'), value: 'many-to-many' }
    ];

    // Filter out User Profile and prevent selecting the same entity in both fields
    const isFromRequired = !newRelation.from;
    const isToRequired = !newRelation.to;
    const isAddDisabled = isFromRequired || isToRequired || !!error;

    function handleAdd(e) {
        const relationExists = relations.some(relation => relation.from === newRelation.from && relation.to === newRelation.to);
        const numSelectedRelations = relations.filter(relation => relation.selected).length;
        if (relationExists) {
            setAddRelationError(t('errors.relation_already_exists'));
        } else if (numSelectedRelations >= maxRelations) {
            setAddRelationError(t('errors.max_relations_exceeded'));
        } else {
            onAdd(e)
        }
    }

    return (
        <Dialog
            title={null}
            onClose={onClose}
            width={480}
            className={styles['add-relation-dialog']}
        >
            <div className={`d-flex justify-content-between align-items-center mb-2`}>
                <span className={styles['dialog-title']}>{t('add_new_relation')}</span>
                <button className={styles['dialog-close-btn']} aria-label="Close" onClick={onClose} type="button">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <div className={`mb-2 px-2`}>
                <label className='form-label'>
                    {t('from_object')} <span className={styles['text-danger']}>*</span>
                </label>
                <ComboBox
                    className={`form-control-custom ${styles['w-100']}`}
                    data={entityOptions}
                    value={entityOptions.find(opt => opt.value === newRelation.from) || null}
                    onChange={e => onChange('from', e.value?.value || '')}
                    textField="text"
                    dataItemKey="value"
                    style={{ width: '100%' }}
                    disabled={entityOptions.length === 0}
                    clearButton={true}
                />
            </div>
            <div className={`mb-2 px-2`}>
                <label className='form-label'>
                    {t('to_object')} <span className={styles['text-danger']}>*</span>
                </label>
                <ComboBox
                    className={`form-control-custom ${styles['w-100']}`}
                    data={entityOptions}
                    value={entityOptions.find(opt => opt.value === newRelation.to) || null}
                    onChange={e => onChange('to', e.value?.value || '')}
                    textField="text"
                    dataItemKey="value"
                    style={{ width: '100%' }}
                    disabled={entityOptions.length === 0}
                    clearButton={true}
                />
            </div>
            <div className={`mb-2 px-2`}>
                <label className='form-label'>{t('relation_type.label')}</label>
                <ComboBox
                    className={styles['w-100']}
                    data={relationTypeOptions}
                    value={relationTypeOptions.find(opt => opt.value === newRelation.type) || relationTypeOptions[0]}
                    onChange={e => onChange('type', e.value?.value || '')}
                    textField="text"
                    dataItemKey="value"
                    style={{ width: '100%' }}
                />
            </div>
            {addRelationError && (
                <div className={`mb-2 px-2`}>
                    <div className="text-danger" role="alert">
                        {addRelationError}
                    </div>
                </div>
            )}
            {newRelation.type === 'one-to-many' && referenceAttributes.length > 0 && (
                <>
                    <div className={`mb-2 px-2`}>
                        <Checkbox
                            type="checkbox"
                            id="setReferenceAttribute"
                            checked={newRelation.setReferenceAttribute}
                            value={newRelation.setReferenceAttribute}
                            onChange={e => onChange('setReferenceAttribute', !newRelation.setReferenceAttribute)}
                            label={t('set_reference_attribute')}
                        />
                    </div>
                    {newRelation.setReferenceAttribute && (
                        <div className={`mb-2 px-2`}>
                            <label className='form-label'>
                                {t('reference_attribute')}
                            </label>
                            <ComboBox
                                className={styles['w-100']}
                                data={referenceAttributes.map(attr => ({ text: attr, value: attr }))}
                                value={referenceAttributes.includes(referenceAttribute) ? { text: referenceAttribute, value: referenceAttribute } : null}
                                onChange={e => onReferenceAttributeChange(e.value?.value || '')}
                                textField="text"
                                dataItemKey="value"
                                style={{ width: '100%' }}
                                disabled={referenceAttributes.length === 0}
                                placeholder={referenceAttributes.length === 0 ? t('no_attributes') : ''}
                            />
                        </div>
                    )}
                </>
            )}
            <div className={`d-flex justify-content-between align-items-center mt-4`}>
                <div className={`text-danger small`} role="alert">
                    {error}
                </div>
                <div className={`d-flex gap-2`}>
                    <button className='btn btn-secondary' onClick={onClose} type="button">
                        {t('cancel')}
                    </button>
                    <button className='btn btn-primary' onClick={handleAdd} disabled={isAddDisabled} type="button">
                        {t('add')}
                    </button>
                </div>
            </div>
        </Dialog>
    );
}

export default AddRelationDialog;
